/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState } from 'react';
import { requestTimeSeriesDataset } from '@/api/eeCheckApi';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import {
    getTimeSeriesSensorNotFoundMessage,
    getTimeSeriesSensorNotNumericMessage,
} from '@/lib/sensors/presentation/sensorRequestMessages';
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import {
    aggregateTimeSeriesValues,
    createTimeSeriesCalcValuesWithAliases,
    findTimeSeriesValueColumn,
    getTimeSeriesAggregationDate,
    normalizeTimeSeriesUrn,
    type TimeSeriesCalc,
    type TimeSeriesAggregation,
    type TimeSeriesTrafficLightConfig,
    type TimeSeriesValueLabelsConfig,
} from '@/lib/timeSeries/timeSeriesValue';
import type { TimeSeriesRequest } from '@/types/eeCheck';
import TimeSeriesValueCard from '@/components/kpi/TimeSeriesValueCard';

interface KpiValueApi2TimeSeriesProps {
    aggregation: TimeSeriesAggregation;
    calc?: TimeSeriesCalc;
    calcCode?: string;
    conversion?: number;
    display?: {
        conversion?: number;
        fractionDigits?: number;
        offset?: number;
        series: string;
        unit?: string | null;
        valueLabels?: TimeSeriesValueLabelsConfig;
    };
    debug?: boolean;
    from: string;
    fractionDigits?: number;
    info?: string;
    link?: string;
    legendCode?: string;
    legendNote?: string;
    legendSeries?: string;
    legendTrafficLight?: TimeSeriesTrafficLightConfig;
    metaText?: string | null;
    modal?: {
        buttonLabel?: string;
        message: string;
        title?: string;
    };
    offset?: number;
    series?: string;
    showLegend?: boolean;
    showDate?: boolean;
    title?: string;
    to: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    trimFractionDigitsForIntegers?: boolean;
    transformValue?: (value: number) => number;
    unit?: string | null;
    urn: string | string[];
    valueLabels?: TimeSeriesValueLabelsConfig;
}

function KpiValueApi2TimeSeries({
    aggregation,
    calc,
    calcCode,
    conversion = 1,
    display,
    debug = false,
    from,
    fractionDigits,
    info,
    link,
    legendCode,
    legendNote,
    legendSeries,
    legendTrafficLight,
    metaText,
    modal,
    offset = 0,
    series,
    showLegend = false,
    showDate = false,
    title,
    to,
    trafficLight,
    trimFractionDigitsForIntegers = false,
    transformValue,
    unit,
    urn,
    valueLabels,
}: KpiValueApi2TimeSeriesProps) {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [value, setValue] = useState<number | null>(null);
    const [displaySeriesValue, setDisplaySeriesValue] = useState<number | null>(null);
    const [legendValue, setLegendValue] = useState<number | null>(null);
    const [valueDate, setValueDate] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const normalizedUrn = normalizeTimeSeriesUrn(urn);
    const requestedUrns = Array.isArray(urn)
        ? urn
        : urn.split(',').map((currentUrn) => currentUrn.trim()).filter(Boolean);
    const legendSensor = legendSeries && requestedUrns.includes(legendSeries)
        ? legendSeries
        : series && requestedUrns.includes(series)
            ? series
            : requestedUrns[0];
    const legendDisplaySensor = display?.series && requestedUrns.includes(display.series) && display.series !== legendSensor
        ? display.series
        : undefined;

    useEffect(() => {
        if (isSessionExpired) {
            setIsLoading(false);
            return;
        }

        if (!accessToken || !normalizedUrn || !from || !to) {
            setValue(null);
            setDisplaySeriesValue(null);
            setLegendValue(null);
            setValueDate(null);
            setIsLoading(false);
            setErrorMessage(t('Missing time series request parameters.'));
            return;
        }

        const payload: TimeSeriesRequest = {
            from,
            to,
            urn: normalizedUrn,
        };

        let isCancelled = false;

        async function loadValue(): Promise<void> {
            setIsLoading(true);
            setLoadingLabel(t('Loading API request...'));
            setErrorMessage('');

            try {
                const { data } = await requestTimeSeriesDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                if (typeof data !== 'string') {
                    setValue(null);
                    setDisplaySeriesValue(null);
                    setLegendValue(null);
                    setValueDate(null);
                    setErrorMessage(t('Time series response is not a CSV string.'));
                    return;
                }

                const parsedTimeSeries = parseTimeSeriesCsv(data);
                const aggregatedValues: Record<string, number | null> = {};
                const aggregatedDates: Record<string, string | null> = {};

                requestedUrns.forEach((currentUrn) => {
                    const valueColumn = findTimeSeriesValueColumn(parsedTimeSeries, currentUrn);

                    if (!valueColumn) {
                        aggregatedValues[currentUrn] = null;
                        aggregatedDates[currentUrn] = null;
                        return;
                    }

                    const yAxisValues = parsedTimeSeries.plotYAxisValues[valueColumn] ?? [];
                    aggregatedValues[currentUrn] = aggregateTimeSeriesValues(
                        yAxisValues,
                        aggregation,
                    );
                    aggregatedDates[currentUrn] = showDate
                        ? getTimeSeriesAggregationDate(
                            yAxisValues,
                            parsedTimeSeries.plotXAxisValues,
                            aggregation,
                        )
                        : null;
                });

                const aggregatedValuesWithAliases = createTimeSeriesCalcValuesWithAliases(aggregatedValues);
                const computedValues = calc
                    ? {
                        ...aggregatedValuesWithAliases,
                        ...calc({ values: aggregatedValuesWithAliases }),
                    }
                    : aggregatedValuesWithAliases;
                const selectedSeries = series ?? requestedUrns[0] ?? null;

                if (!selectedSeries) {
                    setValue(null);
                    setDisplaySeriesValue(null);
                    setLegendValue(null);
                    setValueDate(null);
                    setErrorMessage(t('Missing time series request parameters.'));
                    return;
                }

                if (!(selectedSeries in computedValues)) {
                    setValue(null);
                    setDisplaySeriesValue(null);
                    setLegendValue(null);
                    setValueDate(null);
                    setErrorMessage(getTimeSeriesSensorNotFoundMessage(t, selectedSeries));
                    return;
                }

                const aggregatedValue = computedValues[selectedSeries] ?? null;
                const selectedDisplaySeries =
                    display?.series && display.series in computedValues
                        ? display.series
                        : selectedSeries;
                const selectedDisplayValue = computedValues[selectedDisplaySeries] ?? null;

                if (aggregatedValue === null) {
                    setValue(null);
                    setDisplaySeriesValue(null);
                    setLegendValue(null);
                    setValueDate(null);
                    setErrorMessage(getTimeSeriesSensorNotNumericMessage(t, selectedSeries));
                    return;
                }

                setValue(aggregatedValue);
                setDisplaySeriesValue(selectedDisplayValue);
                setLegendValue(
                    legendSeries && legendSeries in computedValues
                        ? computedValues[legendSeries] ?? null
                        : aggregatedValue,
                );
                setValueDate(
                    showDate
                        ? (
                            aggregatedDates[selectedDisplaySeries] ??
                            aggregatedDates[selectedSeries] ??
                            Object.values(aggregatedDates).find((currentDate) => currentDate !== null) ??
                            null
                        )
                        : null,
                );
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                setValue(null);
                setDisplaySeriesValue(null);
                setLegendValue(null);
                setValueDate(null);
                setErrorMessage(
                    error instanceof Error
                        ? error.message
                        : t('Time series request failed.'),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                    setLoadingLabel('');
                }
            }
        }

        void loadValue();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, aggregation, calc, display?.series, from, isSessionExpired, legendSeries, normalizedUrn, onSessionExpired, series, showDate, t, to]);

    return (
        <TimeSeriesValueCard
            conversion={debug ? 1 : (display?.conversion ?? conversion)}
            displayValue={debug && value !== null ? String(value) : null}
            errorMessage={errorMessage}
            fractionDigits={display?.fractionDigits ?? fractionDigits}
            info={debug ? undefined : info}
            isLoading={isLoading}
            legendConversion={debug ? 1 : conversion}
            legendCode={debug || !showLegend
                ? undefined
                : (legendCode ?? (calc ? calcCode : undefined))}
            legendCodeMissingMessage={debug || !showLegend || legendCode || !calc || calcCode
                ? undefined
                : t('The calculation code is not available for this calculated value.')}
            legendFractionDigits={debug ? undefined : fractionDigits}
            legendNote={debug || !showLegend
                ? undefined
                : (legendNote ?? (
                    calc
                        ? t(calcCode
                            ? 'The displayed and classified value was calculated with "calc" as follows:'
                            : 'The displayed value was calculated with calc.')
                        : undefined
                ))}
            legendOffset={debug ? 0 : offset}
            legendDisplaySensor={debug ? undefined : legendDisplaySensor}
            legendSensor={debug ? undefined : legendSensor}
            legendTrafficLight={debug ? undefined : legendTrafficLight}
            legendTrafficLightValue={debug ? undefined : legendValue}
            legendUnit={debug ? null : (unit ?? null)}
            loadingLabel={loadingLabel}
            link={link}
            metaText={debug ? null : metaText}
            metaDate={debug ? null : (showDate ? valueDate : null)}
            modal={modal}
            offset={debug ? 0 : (display?.offset ?? offset)}
            showLegend={debug ? false : showLegend}
            title={title}
            trafficLight={debug ? undefined : trafficLight}
            trafficLightValue={value}
            trimFractionDigitsForIntegers={trimFractionDigitsForIntegers}
            transformValue={debug ? undefined : transformValue}
            unit={debug ? null : (display?.unit ?? unit)}
            value={debug ? value : (display ? displaySeriesValue : value)}
            valueLabels={debug ? undefined : (display?.valueLabels ?? valueLabels)}
        />
    );
}

export default KpiValueApi2TimeSeries;
